import type { TokenRequest } from "ably"
import { Mutex } from "async-mutex"
import type { FetchArgs } from "@reduxjs/toolkit/query/react"
import { createApi, retry } from "@reduxjs/toolkit/query/react"
import { backoff, getResponseCode, retryCondition } from "@holigral/shared/api"
import fetchBaseQuery from "./fetchBaseQuery"
import { clearAuth, updateTokens } from "@/auth/slice"
import type { TokenResponse } from "@/auth/model"
import { getRefreshToken } from "@/auth/refreshTokenStorage"

export type BaseQueryExtraOptions = {
  readonly withoutAuth?: boolean
}

type Assert = (condition: boolean) => asserts condition

const mutex = new Mutex()

const baseQuery = retry(
  async (
    args: string | FetchArgs,
    api,
    extraOptions?: BaseQueryExtraOptions,
  ) => {
    const { dispatch, endpoint } = api
    const isTokenRefresh = endpoint === "tokenRefresh"

    const baseQuery = fetchBaseQuery({
      withoutAuth: !!extraOptions?.withoutAuth,
    })

    if (!isTokenRefresh) {
      // Wait for a possible concurrent token refresh
      await mutex.waitForUnlock()
    }

    const result = await baseQuery(args, api, extraOptions ?? {})

    // 401 suggests token has expired, so we try to fetch a new one – or clear auth state if it fails
    // If mutex is locked then another request is already refreshing so we let this one fail and go for retry
    // This be tightly coupled with retry options below – backoff and retryCondition
    if (
      getResponseCode(result.error) === 401 &&
      !mutex.isLocked() &&
      !isTokenRefresh
    ) {
      // Lock and attempt token refresh
      const release = await mutex.acquire()
      try {
        const giveUp = (): never => {
          dispatch(clearAuth())
          retry.fail(result.error)
        }

        const assert: Assert = (condition) => {
          if (!condition) {
            giveUp()
          }
        }

        const refreshToken = getRefreshToken()
        assert(!!refreshToken)

        try {
          // Refresh token
          await dispatch(
            baseApi.endpoints.tokenRefresh.initiate(refreshToken),
          ).unwrap()
        } catch {
          giveUp()
        }
      } finally {
        release()
      }
    }

    return result
  },
  {
    backoff,
    retryCondition,
  },
)

const baseApi = createApi({
  baseQuery,
  refetchOnMountOrArgChange: 60,
  refetchOnReconnect: true,
  endpoints: (build) => ({
    tokenRefresh: build.mutation<TokenResponse, string>({
      query: (payload) => ({
        url: "security/refresh",
        method: "POST",
        body: { refreshToken: payload },
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(updateTokens(data))
        } catch {
          // noop
        }
      },
    }),
    ablyAuth: build.mutation<TokenRequest, void>({
      query: () => ({
        url: "ably-auth",
        method: "POST",
      }),
    }),
  }),
})

export type BaseQuery = typeof baseQuery

export const { useTokenRefreshMutation, useAblyAuthMutation } = baseApi

export default baseApi
